import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, DatePicker, Flex, Form, Input, InputNumber, Radio, Row, Select, Space, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { SkuSelect } from "../catalog/mapping";
import { useConnections } from "../channels/hooks";
import { rupiahInput } from "../procurement/common";

type Payment = "unpaid" | "paid" | "cod";
type FormLine = { skuId?: string; quantity?: number; unitPrice?: number };
type Values = {
  connectionId?: string;
  buyerName: string;
  recipientName?: string;
  recipientPhone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  lines: FormLine[];
  shippingFee?: number;
  discount?: number;
  paymentStatus: Payment;
  paidAccountId?: string;
  carrier?: string;
  trackingNo?: string;
  shipBy?: Dayjs | null;
  note?: string;
};

/**
 * Buat order manual (PRD OMS-5): a sale from brand.com, social media or the
 * shop floor. It follows the usual path; unpaid ones wait until paid.
 */
export function ManualOrderNew() {
  const { t, formatMoney } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  useTabTitle(t("manual.title"));
  const connections = useConnections();
  const channels = (connections.data?.items ?? []).filter((c) => c.platform === "manual" && c.status !== "disconnected");
  const lines = Form.useWatch("lines", form) ?? [];
  const payment = Form.useWatch("paymentStatus", form);
  const shippingFee = Form.useWatch("shippingFee", form) ?? 0;
  const discount = Form.useWatch("discount", form) ?? 0;
  const accounts = useQuery({
    queryKey: ["finance", "cash", "accounts"],
    queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")),
    enabled: can("finance.view"),
  });
  const subtotal = lines.reduce((s, l) => s + (l?.quantity ?? 0) * (l?.unitPrice ?? 0), 0);
  const total = subtotal + shippingFee - discount;

  const save = useMutation({
    mutationFn: (v: Values) =>
      unwrap(
        api.POST("/v1/orders/manual", {
          body: {
            connectionId: v.connectionId!,
            buyerName: v.buyerName.trim(),
            recipientName: v.recipientName?.trim() ?? "",
            recipientPhone: v.recipientPhone?.trim() ?? "",
            address: v.address?.trim() ?? "",
            city: v.city?.trim() ?? "",
            postalCode: v.postalCode?.trim() ?? "",
            lines: v.lines.filter((l) => l.skuId && l.quantity).map((l) => ({ skuId: l.skuId!, quantity: l.quantity!, unitPrice: l.unitPrice ?? 0 })),
            shippingFee: v.shippingFee ?? 0,
            discount: v.discount ?? 0,
            paymentStatus: v.paymentStatus,
            paidAccountId: v.paymentStatus === "paid" ? (v.paidAccountId ?? null) : null,
            carrier: v.carrier?.trim() ?? "",
            trackingNo: v.trackingNo?.trim() ?? "",
            shipBy: v.shipBy ? v.shipBy.endOf("day").toISOString() : null,
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: (o) => {
      void message.success(o.hold === "unpaid" ? t("manual.createdUnpaid", { number: o.externalId }) : t("manual.created", { number: o.externalId }));
      for (const k of ["orders", "inventory", "finance"]) void queryClient.invalidateQueries({ queryKey: [k] });
      navigate(`/app/orders/${o.id}`, { replace: true });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  if (!connections.isPending && channels.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        title={t("manual.noChannel")}
        action={
          can("channels.manage") && (
            <Button type="primary" onClick={() => navigate("/app/channels")}>
              {t("channels.manual.add")}
            </Button>
          )
        }
      />
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={{ paymentStatus: "unpaid", lines: [{ quantity: 1 }], connectionId: channels.length === 1 ? channels[0]?.id : undefined }}
      onFinish={(v) => save.mutate(v)}
    >
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("manual.title")}
        </Typography.Title>
        <Space>
          <Button onClick={() => navigate("/app/orders")}>{t("common.cancel")}</Button>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            {t("manual.save")}
          </Button>
        </Space>
      </Flex>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card size="small" title={t("manual.buyer")} style={{ marginBottom: 16 }}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name="connectionId" label={t("manual.channel")} rules={[{ required: true, message: t("field.required") }]}>
                  <Select options={channels.map((c) => ({ value: c.id, label: c.name }))} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="buyerName" label={t("manual.buyerName")} rules={[{ required: true, min: 2, message: t("field.required") }]}>
                  <Input maxLength={120} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="recipientName" label={t("manual.recipientName")} extra={t("manual.recipientHelp")}>
                  <Input maxLength={120} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="recipientPhone" label={t("manual.phone")}>
                  <Input maxLength={30} inputMode="tel" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="address" label={t("manual.address")}>
                  <Input.TextArea rows={2} maxLength={500} />
                </Form.Item>
              </Col>
              <Col xs={16} md={12}>
                <Form.Item name="city" label={t("manual.city")}>
                  <Input maxLength={120} />
                </Form.Item>
              </Col>
              <Col xs={8} md={6}>
                <Form.Item name="postalCode" label={t("manual.postalCode")}>
                  <Input maxLength={10} inputMode="numeric" />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          <Card size="small" title={t("manual.items")}>
            <Form.List name="lines">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((f) => {
                    const l = lines[f.name] ?? {};
                    return (
                      <Flex key={f.key} gap={8} align="start" wrap>
                        <Form.Item name={[f.name, "skuId"]} style={{ flex: "1 1 260px" }} rules={[{ required: true, message: t("field.required") }]}>
                          <SkuSelect />
                        </Form.Item>
                        <Form.Item name={[f.name, "quantity"]} rules={[{ required: true, message: t("field.required") }]}>
                          <InputNumber min={1} max={100_000} precision={0} placeholder={t("po.quantity")} style={{ width: 100 }} />
                        </Form.Item>
                        <Form.Item name={[f.name, "unitPrice"]} rules={[{ required: true, message: t("field.required") }]}>
                          <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" placeholder={t("manual.price")} style={{ width: 160 }} {...rupiahInput} />
                        </Form.Item>
                        <Typography.Text className="z-num" style={{ width: 130, textAlign: "right", paddingTop: 5 }}>
                          {formatMoney((l.quantity ?? 0) * (l.unitPrice ?? 0))}
                        </Typography.Text>
                        {fields.length > 1 && <Button type="text" icon={<MinusCircleOutlined />} aria-label={t("common.delete")} onClick={() => remove(f.name)} />}
                      </Flex>
                    );
                  })}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })} block>
                    {t("po.addLine")}
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card size="small" title={t("manual.payment")} style={{ marginBottom: 16 }}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="shippingFee" label={t("manual.shippingFee")}>
                  <InputNumber<number> min={0} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="discount" label={t("manual.discount")}>
                  <InputNumber<number> min={0} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
                </Form.Item>
              </Col>
            </Row>
            <Flex justify="space-between" style={{ marginBottom: 16 }}>
              <Typography.Text strong>{t("manual.total")}</Typography.Text>
              <Typography.Text strong className="z-num">
                {formatMoney(total)}
              </Typography.Text>
            </Flex>
            <Form.Item name="paymentStatus" label={t("manual.paymentStatus")}>
              <Radio.Group
                optionType="button"
                options={(["unpaid", "paid", "cod"] as Payment[]).map((p) => ({ value: p, label: t(`manual.pay.${p}`) }))}
              />
            </Form.Item>
            {payment === "unpaid" && <Typography.Paragraph type="secondary">{t("manual.unpaidHelp")}</Typography.Paragraph>}
            {payment === "paid" && can("finance.view") && (
              <Form.Item name="paidAccountId" label={t("manual.paidInto")} extra={t("manual.paidIntoHelp")}>
                <Select allowClear options={(accounts.data?.items ?? []).filter((a) => a.active).map((a) => ({ value: a.id, label: a.name }))} />
              </Form.Item>
            )}
          </Card>
          <Card size="small" title={t("manual.shipping")}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="carrier" label={t("manual.carrier")}>
                  <Input maxLength={60} placeholder="J&T, JNE, SiCepat…" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="trackingNo" label={t("manual.trackingNo")} extra={t("manual.trackingHelp")}>
                  <Input maxLength={60} className="z-num" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="shipBy" label={t("orders.shipBy")}>
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="note" label={t("po.note")}>
                  <Input.TextArea rows={2} maxLength={1000} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Form>
  );
}
